import marked from 'marked'

export const AGENT_LABELS = {
    assistant: 'AI Assistant',
    analyst: 'Data Analyst',
    sales: 'Sales Assistant'
}

/**
 * The agents available to the given user. The Data Analyst has direct
 * database access, so it is admin-only.
 */
export function availableAgents(isAdmin) {
    const agents = { ...AGENT_LABELS }

    if (!isAdmin) {
        delete agents.analyst
    }

    return agents
}

/**
 * Validate a chat attachment's size, returning an error message or null.
 *
 * Images get a higher limit because the API resizes them before they are
 * added to the conversation; other files are read in as-is.
 */
export function attachmentError(file) {
    const isImage = file.type.startsWith('image/')
    const limit = isImage ? 10 : 2

    if (file.size > limit * 1024 * 1024) {
        return isImage
            ? 'Images are limited to 10MB.'
            : 'Attachments are limited to 2MB. Larger documents belong in the opportunity context files.'
    }

    return null
}

/**
 * The icon for an uploaded context file kind.
 */
export function fileIcon(kind) {
    return {
        audio: 'fa-microphone',
        image: 'fa-image',
        document: 'fa-file-pdf'
    }[kind] || 'fa-file'
}

/**
 * Escape assistant content and render markdown links and bare URLs as
 * anchors. All HTML-significant characters (including quotes) are escaped
 * before the link regexes run, so no captured URL can break out of the
 * href attribute.
 */
export function linkify(content) {
    const escaped = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')

    // Markdown links: [text](url)
    return escaped
        .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
        .replace(/(^|[^"'>])(https?:\/\/[^\s<]*[^\s<.,)\]!?'"])/g, '$1<a href="$2" target="_blank" rel="noopener">$2</a>')
}

const markdownRenderer = new marked.Renderer()

// Only http(s) links, opened in a new tab; anything else renders as text.
markdownRenderer.link = (href, title, text) => {
    if (!/^https?:\/\//i.test(href || '')) {
        return text
    }

    return '<a href="' + href.replace(/"/g, '%22') + '" target="_blank" rel="noopener">' + text + '</a>'
}

// Never load remote images from model output - show the alt text instead.
markdownRenderer.image = (href, title, text) => text || ''

/**
 * Render assistant markdown safely. Raw HTML in the content is escaped
 * BEFORE markdown parsing, so the model can only produce formatting through
 * markdown syntax - never by emitting markup directly.
 */
export function renderMarkdown(content) {
    const escaped = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

    return marked(escaped, {
        renderer: markdownRenderer,
        gfm: true,
        breaks: true,
        headerIds: false
    })
}

/**
 * Render a chat message: assistant replies as markdown, user messages as
 * escaped plain text with clickable links.
 */
export function renderMessage(message) {
    return message.role === 'assistant'
        ? renderMarkdown(message.content)
        : linkify(message.content)
}
